import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3 } from 'lucide-react';
import clsx from 'clsx';
import { EmptyState } from '@/components/ui';

interface ChartCardProps {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /** When true, renders an empty state instead of the children. */
  isEmpty?: boolean;
  emptyMessage?: ReactNode;
  className?: string;
  children?: ReactNode;
}

/** Card frame shared by every report chart: title row, body, empty state. */
export function ChartCard({
  title,
  description,
  actions,
  isEmpty,
  emptyMessage,
  className,
  children,
}: ChartCardProps) {
  const { t } = useTranslation();
  return (
    <div className={clsx('card min-w-0 p-5', className)}>
      {(title || actions) && (
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title && <h3 className="card-title">{title}</h3>}
            {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {isEmpty ? (
        <EmptyState
          icon={BarChart3}
          title={emptyMessage ?? t('charts.noData')}
          description={t('charts.noDataHint')}
        />
      ) : (
        children
      )}
    </div>
  );
}
